<script setup>
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';
import { useTranslationView } from 'dashboard/composables/useTranslationView';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import locales from 'shared/constants/locales';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
});

const PAGE_SIZE = 5;

const store = useStore();
const { t } = useI18n();
const { uiSettings } = useUISettings();
const { currentAccount } = useAccount();
const { showOriginals, toggleOriginals, resetTranslationView } =
  useTranslationView();

const isTranslating = ref(false);
const beforeMessageId = ref(null);
const hasMore = ref(true);
// Whether anything in this thread has been translated during this visit. The
// originals toggle is meaningless before that.
const hasTranslated = ref(false);

const integration = computed(() => {
  const smart = store.getters['integrations/getIntegration']('smart_translate');
  if (smart.enabled) return smart;
  return store.getters['integrations/getIntegration']('google_translate');
});

const isEnabled = computed(() => Boolean(integration.value.enabled));

const additionalAttributes = computed(
  () => props.chat.additional_attributes || {}
);

const conversationLanguage = computed(
  () => additionalAttributes.value.conversation_language || ''
);

// An agent-declared language wins over detection, and the picker has to show
// which of the two the thread is currently on.
const isManualLanguage = computed(
  () => additionalAttributes.value.conversation_language_source === 'manual'
);

// Empty means "keep detecting"; a value means the agent declared it.
const selectedLanguage = computed(() =>
  isManualLanguage.value ? conversationLanguage.value : ''
);

const accountLocale = computed(() => currentAccount.value?.locale || 'en');

const agentLanguage = computed(
  () => uiSettings.value?.locale || accountLocale.value || 'en'
);

// "pt_BR" and "pt" are the same language for the purpose of deciding whether
// anything needs translating.
const baseLanguage = code => String(code).split(/[-_]/)[0].toLowerCase();

const needsTranslation = computed(() => {
  if (!conversationLanguage.value) return false;
  return (
    baseLanguage(conversationLanguage.value) !==
    baseLanguage(agentLanguage.value)
  );
});

const languageName = code => locales[code] || locales[baseLanguage(code)] || code;

const autoLabel = computed(() => {
  if (!conversationLanguage.value)
    return t('CONVERSATION.TRANSLATION.AUTO_DETECT');
  return t('CONVERSATION.TRANSLATION.AUTO_DETECTED', {
    language: languageName(conversationLanguage.value),
  });
});

const languageOptions = computed(() =>
  // The long locale list is unusable in a header dropdown; base languages are
  // what a support team actually picks from.
  Object.keys(locales)
    .filter(code => !code.includes('_'))
    .map(code => ({ value: code, label: locales[code] }))
    .sort((a, b) => a.label.localeCompare(b.label))
);

// A different conversation means a fresh translation cursor and view.
watch(
  () => props.chat.id,
  () => {
    beforeMessageId.value = null;
    hasMore.value = true;
    hasTranslated.value = false;
    resetTranslationView();
  }
);

const translatePage = async () => {
  isTranslating.value = true;
  try {
    const result = await store.dispatch('bulkTranslateMessages', {
      conversationId: props.chat.id,
      targetLanguage: agentLanguage.value,
      limit: PAGE_SIZE,
      beforeMessageId: beforeMessageId.value,
    });
    beforeMessageId.value = result.beforeMessageId;
    hasMore.value = result.hasMore;
    hasTranslated.value = true;
  } catch (error) {
    useAlert(parseAPIErrorResponse(error));
  } finally {
    isTranslating.value = false;
  }
};

const changeLanguage = async event => {
  try {
    await store.dispatch('updateTranslationSettings', {
      conversationId: props.chat.id,
      conversationLanguage: event.target.value,
    });
  } catch (error) {
    useAlert(parseAPIErrorResponse(error));
  }
};

const translateLabel = computed(() => {
  if (isTranslating.value) return t('CONVERSATION.TRANSLATION.TRANSLATING');
  if (beforeMessageId.value)
    return t('CONVERSATION.TRANSLATION.TRANSLATE_MORE', { count: PAGE_SIZE });
  return t('CONVERSATION.TRANSLATION.TRANSLATE_LATEST', { count: PAGE_SIZE });
});
</script>

<template>
  <div v-if="isEnabled" class="flex items-center gap-1">
    <select
      v-tooltip="$t('CONVERSATION.TRANSLATION.LANGUAGE_TOOLTIP')"
      :value="selectedLanguage"
      class="!h-8 !py-0 !mb-0 !text-xs !w-36 !bg-n-alpha-2 !border-n-weak text-n-slate-11"
      @change="changeLanguage"
    >
      <option value="">{{ autoLabel }}</option>
      <option v-for="option in languageOptions" :key="option.value" :value="option.value">
        {{ option.label }}
      </option>
    </select>
    <NextButton
      sm
      ghost
      slate
      icon="i-lucide-languages"
      :label="translateLabel"
      :is-loading="isTranslating"
      :disabled="isTranslating || !hasMore"
      @click="translatePage"
    />
    <NextButton
      v-if="needsTranslation || hasTranslated"
      sm
      ghost
      :slate="!showOriginals"
      :teal="showOriginals"
      icon="i-lucide-eye"
      :label="
        showOriginals
          ? $t('CONVERSATION.TRANSLATION.SHOW_ALL_TRANSLATED')
          : $t('CONVERSATION.TRANSLATION.SHOW_ALL_ORIGINALS')
      "
      @click="toggleOriginals"
    />
  </div>
</template>
